import * as React from 'react';
import { memo } from 'react';

import styled from 'styled-components';

/**
 * Значок медали по её КАТЕГОРИИ (ТЗ 0039, доводка UX).
 *
 * Владелец: «нет картинок и иконок». У модели медали поле `icon` есть, но оно
 * пустое у всех строк и заполнять его вручную никто не будет — поэтому знак
 * берётся из категории, которая у медали есть ВСЕГДА и задаётся правилом, а
 * не оператором. Ничего заводить не надо, и пустых мест не бывает.
 *
 * Рисунки простые и вписаны в 24×24 — это опора для взгляда в сетке из
 * тринадцати карточек, а не иллюстрация. Библиотеку иконок ради пяти фигур
 * не тащим: в ERP `lucide-react` 1.x уже ломал сборку на webpack 4.
 */
export interface IMedalIcon {
  /** `Achievement.category`: learning | skills | profile | career | community. */
  category: string;
  /** Не полученная медаль показывается серой — но показывается. */
  muted?: boolean;
  size?: number;
}

const PATHS: Record<string, string> = {
  // книга — учёба
  learning: 'M4 5a2 2 0 0 1 2-2h11v16H6a2 2 0 0 0-2 2V5zm2 13h9V5H6a1 1 0 0 0 0 2h7v2H6z',
  // столбики роста — навыки
  skills: 'M4 20h3v-7H4v7zm6.5 0h3V4h-3v16zM17 20h3v-11h-3v11z',
  // силуэт — профиль
  profile: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4 0-7 2-7 4.5V21h14v-2.5C19 16 16 14 12 14z',
  // портфель — карьера
  career:
    'M9 4h6a2 2 0 0 1 2 2v1h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h3V6a2 2 0 0 1 2-2zm0 3h6V6H9v1z',
  // две фигуры — сообщество
  community:
    'M8 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 19c0-2.5 2.7-4 6-4s6 1.5 6 4v1H2v-1zm14.2-3.7c2.4.5 4.3 1.8 4.3 3.7v1h-4.7v-1c0-1.4-.6-2.6-1.6-3.5.7-.1 1.3-.2 2-.2z',
};

const COLORS: Record<string, string> = {
  learning: '#3E63DD',
  skills: '#0891B2',
  profile: '#7C3AED',
  career: '#047857',
  community: '#C2410C',
};

const MUTED = '#94A3B8';
const FALLBACK = '#64748B';

const Box = styled.span<{ $size: number; $bg: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
`;

export const MedalIcon = memo(({ category, muted = false, size = 32 }: IMedalIcon) => {
  const color = muted ? MUTED : COLORS[category] || FALLBACK;
  const path = PATHS[category] || PATHS.skills;

  return (
    // Декоративна: рядом всегда стоит название медали, и читалка не должна
    // произносить его дважды.
    <Box $size={size} $bg={`${color}1F`} aria-hidden>
      <svg
        width={Math.round(size * 0.62)}
        height={Math.round(size * 0.62)}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false">
        <path d={path} fill={color} />
      </svg>
    </Box>
  );
});

MedalIcon.displayName = 'MedalIcon';
